import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { FiAlertCircle, FiRefreshCw } from 'react-icons/fi';

const VideoStream = ({ streamKey, isLive }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const initializeStream = () => {
    if (!videoRef.current || !isLive) return;

    const video = videoRef.current;
    const hlsUrl = `https://stream.streamerwars.com/live/${streamKey}/index.m3u8`;

    // Check if HLS is supported
    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeMaxRetry: 5,
        nudgeOffset: 0.1,
        nudgePeriod: 1,
        debug: false
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(error => {
          console.error('Error playing video:', error);
          setError('Failed to play video stream');
        });
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Network error:', data);
              if (retryCount < maxRetries) {
                setRetryCount(prev => prev + 1);
                hls.startLoad();
              } else {
                setError('Network error: Failed to load stream');
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Media error:', data);
              if (retryCount < maxRetries) {
                setRetryCount(prev => prev + 1);
                hls.recoverMediaError();
              } else {
                setError('Media error: Failed to play stream');
              }
              break;
            default:
              console.error('Fatal error:', data);
              setError('Stream playback error');
              hls.destroy();
              break;
          }
        }
      });

      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // For Safari, which has native HLS support
      video.src = hlsUrl;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(error => {
          console.error('Error playing video:', error);
          setError('Failed to play video stream');
        });
      });
    }
  };

  useEffect(() => {
    initializeStream();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [streamKey, isLive, retryCount]);

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    initializeStream();
  };

  if (!isLive) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <span className="text-gray-400">Stream is offline</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white space-y-4">
        <FiAlertCircle className="w-8 h-8 text-red-500" />
        <p className="text-red-500">{error}</p>
        <button
          onClick={handleRetry}
          className="flex items-center space-x-2 bg-purple-600 px-4 py-2 rounded-lg hover:bg-purple-700"
        >
          <FiRefreshCw className="w-5 h-5" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className="w-full h-full object-cover"
      playsInline
      autoPlay
      muted
      controls
    />
  );
};

export default VideoStream; 